import App from 'next/app';
import React, { useEffect } from 'react';
import Script from 'next/script';
import { useRouter } from 'next/router';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
import Draggable from 'gsap/dist/Draggable';
import InertiaPlugin from 'gsap/dist/InertiaPlugin';

if (typeof window !== 'undefined') {
	gsap.registerPlugin(ScrollTrigger, Draggable, InertiaPlugin);
}

//* Providers
import { UIProvider, DataProvider, LanguageProvider, UserProvider } from 'context';

//* Components
import AppHeader from 'components/common/AppHeader';
import AppFooter from 'components/common/AppFooter';
import Popup from 'components/common/Popup';
import CookiePopup from 'components/common/CookiePopup';

//* Helpers
import { getData, getPageParams, config, getUser } from 'helpers';

//* Styles
import 'styles/antd.normalize.css';
import 'swiper/css';
import 'styles/icons/style.css';

const GA_TRACKING_ID = 'G-EKWGTP62J3';

function MyAppWrapper({ Component, pageProps, router, userProps }) {
	const nextRouter = useRouter();

	useEffect(() => {
		if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
			window.addEventListener('load', () => {
				navigator.serviceWorker
					.register('/service-worker.js')
					.then((registration) => {
						console.log('TRAVEL ARMENIA service worker registered');
					})
					.catch((error) => {
						console.error('Service worker registration failed:');
					});
			});
		}
	}, []);

	useEffect(() => {
		const handleRouteChange = (url) => {
			if (window.gtag) {
				window.gtag('config', GA_TRACKING_ID, {
					page_path: url,
				});
			}
		};
		nextRouter.events.on('routeChangeComplete', handleRouteChange);
		return () => {
			nextRouter.events.off('routeChangeComplete', handleRouteChange);
		};
	}, [nextRouter.events]);

	return (
		<>
			{process.env.NODE_ENV === 'production' && (
				<>
					<Script
						src={`https://www.googletagmanager.com/gtag/js?id=${GA_TRACKING_ID}`}
						strategy='afterInteractive'
					/>
					<Script
						id='gtag-init'
						strategy='afterInteractive'
						dangerouslySetInnerHTML={{
							__html: `
								window.dataLayer = window.dataLayer || [];
								function gtag(){dataLayer.push(arguments);}
								gtag('js', new Date());
								gtag('config', '${GA_TRACKING_ID}');
							`,
						}}
					/>
					<Script
						src='https://heymoby.ai/widget/v3/chat.js'
						strategy='afterInteractive'
						onLoad={() => {
							// Wait a bit for the script to fully initialize
							setTimeout(() => {
								if (typeof window !== 'undefined' && window.MobyChat) {
									try {
										window.MobyChat.init('mmq9k');
									} catch (error) {
										console.error('Failed to initialize Moby chat:', error);
									}
								} else {
									console.warn('MobyChat not available after script load');
								}
							}, 100);
						}}
						onError={(e) => {
							console.error('Failed to load Moby chat script:', e);
						}}
					/>
				</>
			)}

			<LanguageProvider>
				<UIProvider>
					<UserProvider userProps={userProps}>
						<DataProvider newData={pageProps}>
							{router.pathname !== '/404' && router.pathname !== '/uikit' && router.pathname !== '/reset-password' && pageProps.pageParams?.path !== config.routes.singleLanding.path && <AppHeader transparentHeader={pageProps.pageParams?.transparentHeader} />}

							<Component
								{...pageProps}
								config={config.routes}
								selectedLang={router.locale}
							/>

							<Popup />
							<CookiePopup />

							{router.pathname !== '/404' && router.pathname !== '/uikit' && router.pathname !== '/reset-password' && router.pathname !== '/map' && pageProps.pageParams?.path !== config.routes.singleLanding.path && <AppFooter />}
						</DataProvider>
					</UserProvider>
				</UIProvider>
			</LanguageProvider>
		</>
	);
}

class MyApp extends App {
	static async getInitialProps({ ctx, router }) {
		if (!process.browser) {
			const newState = {
				pages: {},
				global: {},
			};

			let userData = {
				isLoggedIn: false,
			};

			userData = await getUser(router.locale, userData, ctx);
			const data = await getData({ lang: ctx.locale, state: newState, router: ctx, getPageParams: true });

			return {
				pageProps: {
					...data,
					browser: false,
				},
				userProps: userData.newState,
			};
		} else {
			return {
				pageProps: {
					pageParams: getPageParams({ lang: ctx.locale, router: ctx }),
					browser: true,
				},
			};
		}
	}

	render() {
		return <MyAppWrapper {...this.props} />;
	}
}

export default MyApp;
